import React, { useContext } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, Calendar, Phone, ArrowLeft, Info } from "lucide-react";
import PageHero from "@/components/PageHero";
import { SERVICES, CLINIC, localiseService } from "@/lib/data";
import { BookContext } from "@/components/Layout";
import { useLang } from "@/lib/i18n";

export default function ServiceDetail() {
  const { slug } = useParams();
  const raw = SERVICES.find((s) => s.slug === slug);
  const { open } = useContext(BookContext);
  const { t, lang } = useLang();

  if (!raw) return <Navigate to="/services" replace />;

  const svc = localiseService(raw, lang);
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3).map((s) => localiseService(s, lang));

  return (
    <div data-testid={`svc-detail-${slug}`}>
      <PageHero
        eyebrow={svc.short}
        title={svc.name}
        subtitle={svc.blurb}
        crumbs={[{ label: t("nav.services"), to: "/services" }, { label: svc.name }]}
        image={svc.image}
      />

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-10">
            <div className="rounded-3xl overflow-hidden border border-black/5 shadow-soft">
              <img src={svc.image} alt={svc.name} className="w-full aspect-[16/9] object-cover" />
            </div>

            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("svc.offer.eyebrow")}</div>
              <h2 className="font-display text-3xl text-deep font-semibold mb-5">{t("svc.offer.title")}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {svc.details.map((d) => (
                  <div key={d} className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-black/5">
                    <CheckCircle2 className="w-5 h-5 text-brand shrink-0" strokeWidth={1.6} />
                    <span className="text-sm text-deep font-medium">{d}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-7 bg-brand/5 rounded-3xl border border-brand/10">
              <div className="flex items-center gap-3 mb-3">
                <Info className="w-5 h-5 text-brand" strokeWidth={1.6} />
                <h3 className="font-display text-xl text-deep font-semibold">{t("svc.prep.title")}</h3>
              </div>
              <p className="text-ink-soft text-sm leading-relaxed">{svc.preparation}</p>
            </div>

            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-brand"><ArrowLeft className="w-4 h-4" /> {t("svc.allServices")}</Link>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 bg-deep text-white p-7 rounded-3xl shadow-lifted">
              <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("svc.reserve.eyebrow")}</div>
              <h3 className="font-display text-2xl font-semibold mb-2">{svc.name}</h3>
              <p className="text-white/70 text-sm mb-6">{t("svc.reserve.desc")}</p>
              <button onClick={() => open(svc.name)} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-gold text-deep rounded-full font-semibold hover:bg-gold-600 transition-colors" data-testid="svc-book-btn">
                <Calendar className="w-4 h-4" /> {t("cta.book")}
              </button>
              <a href={`tel:${CLINIC.phone}`} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-white/5 border border-white/15 text-white rounded-full font-semibold hover:bg-white/10 transition-colors">
                <Phone className="w-4 h-4" /> {CLINIC.phone}
              </a>
              <div className="mt-7 pt-7 border-t border-white/10 text-xs text-white/60 space-y-2">
                <div>{t("common.timings.value")}</div>
                <div>{CLINIC.address}</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-background border-t border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-display text-2xl sm:text-3xl text-deep font-semibold mb-8">{t("svc.related")}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group bg-white rounded-3xl overflow-hidden border border-black/5 shadow-soft hover:shadow-lifted hover:-translate-y-1 transition-all">
                <img src={s.image} alt={s.name} className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-1">{s.short}</div>
                  <div className="font-display text-xl text-deep font-semibold">{s.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
